import { exceedsLimit } from './composerRules';

// Composer lines end in <br> or '\n', or start a <div>.
// Each break counts as one character, so offsets survive re-wrapping the text.
const LINE_BLOCKS = ['DIV', 'P'];
const BREAK = '\n';

/**
 * Text of the composer as the user sees it, one '\n' per line break.
 *
 * @param {Node} root Composer element, or a fragment cut from it.
 * @returns {string} The text.
 */
export const readComposerText = root => {
    let text = '';
    walk(root, {
        onText: node => {
            text += node.nodeValue;
        },
        onBreak: () => {
            text += BREAK;
        },
    });
    return text;
};

/**
 * Caret position as an offset into readComposerText.
 *
 * @param {HTMLElement} root Composer element.
 * @returns {number|null} The offset, or null when the caret is not in the composer.
 */
export const getCaretOffset = root => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;
    const caret = selection.getRangeAt(0);
    if (!root.contains(caret.endContainer)) return null;
    const before = document.createRange();
    before.selectNodeContents(root);
    before.setEnd(caret.endContainer, caret.endOffset);
    return readComposerText(before.cloneContents()).length;
};

/**
 * DOM position of an offset into readComposerText. A position inside a mention chip moves
 * after the chip, which cannot hold the caret.
 *
 * @param {HTMLElement} root Composer element.
 * @param {number} offset Offset into the composer text.
 * @returns {{node: Node, offset: number}} Where to put a range boundary.
 */
export const locateOffset = (root, offset) => {
    let count = 0;
    let found = null;
    let lastNode = null;
    walk(root, {
        onText: node => {
            lastNode = node;
            if (found || count + node.nodeValue.length < offset) {
                count += node.nodeValue.length;
                return;
            }
            const chip = node.parentElement?.closest('[contenteditable="false"]');
            found = chip ? afterNode(chip) : { node, offset: offset - count };
        },
        onBreak: node => {
            lastNode = node;
            if (!found) count += BREAK.length;
        },
    });
    if (found) return found;
    return lastNode ? afterNode(lastNode) : { node: root, offset: root.childNodes.length };
};

/**
 * Puts a collapsed caret at an offset into readComposerText.
 *
 * @param {HTMLElement} root Composer element.
 * @param {number} offset Offset into the composer text.
 */
export const setCaretOffset = (root, offset) => {
    const { node, offset: nodeOffset } = locateOffset(root, offset);
    const range = document.createRange();
    range.setStart(node, nodeOffset);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
};

/**
 * Refuses typed input that would push the composer past 10,000 characters (FR103). Selected
 * text being replaced is not counted; an input method's own text cannot be refused.
 *
 * @param {InputEvent} event React beforeinput event.
 * @param {HTMLElement} root Composer element.
 */
export const refuseOverLimit = (event, root) => {
    if (!root || event.nativeEvent?.isComposing) return;
    const selected = String(window.getSelection() || '');
    if (exceedsLimit(readComposerText(root), event.data, selected)) event.preventDefault();
};

const afterNode = node => {
    const parent = node.parentNode;
    return { node: parent, offset: Array.prototype.indexOf.call(parent.childNodes, node) + 1 };
};

// A line block that follows other content starts a new line; a <br> ends one.
const walk = (node, { onText, onBreak }) => {
    node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
            onText(child);
        } else if (child.nodeName === 'BR') {
            onBreak(child);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            if (LINE_BLOCKS.includes(child.nodeName) && child.previousSibling) onBreak(child);
            walk(child, { onText, onBreak });
        }
    });
};
